//#region import libraries
import React, { useEffect, useState, useRef } from "react";

// custom components
import ActionsModal from "_components/molecules/Modals/actions-modal";
import EmployeeModal from "./employee-modal";
import EmployeeProfilePicture from "./employee-profile-picture";
import AddEmployeeShift from "./add-employee-shift";

// redux
import { useDispatch, useSelector } from "react-redux";
import { selectEmployeeConversations, setSelectedEmployeeProfile, setSelectedForChat, updateEmployee } from "_redux/slices/timeline/employees";
import { selectTimelineData } from "_redux/slices/timeline/timeline-data";
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { selectTimelineAppSettings } from "_redux/slices/timeline/timeline-control";

// styling
import { Colors } from "_styles";
import styled from 'styled-components';

// icons
import { convertTimeStampToDate } from "_utils/functions";
import { FiCoffee, FiLogIn, FiLogOut } from "react-icons/fi";
import { FiChevronRight } from "react-icons/fi";
import { SlOptions } from "react-icons/sl";
import { IoChatbubble } from "react-icons/io5";
import { selectFirebaseInfo, selectFirebaseUser } from "_redux/slices/main-screen/user";

const GlowingIcon = styled(IoChatbubble)`
  margin-top: 5px;
  cursor: pointer;
  color: ${props => props.glow ? 'green' : "gray"};
  animation: ${props => props.glow ? 'glow 2s ease-in-out infinite' : 'none'};
  
  @keyframes glow {
    0% {
      text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 10px rgba(0, 255, 0, 0.2);
      filter: drop-shadow(0 0 2px rgba(0, 255, 0, 0.4));
    }
    50% {
      text-shadow: 0 0 20px rgba(0, 255, 0, 0.9), 0 0 30px rgba(0, 255, 0, 0.7), 0 0 40px rgba(0, 255, 0, 0.5);
      filter: drop-shadow(0 0 10px rgba(0, 255, 0, 0.9)) drop-shadow(0 0 20px rgba(0, 255, 0, 0.7));
    }
    100% {
      text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 10px rgba(0, 255, 0, 0.2);
      filter: drop-shadow(0 0 2px rgba(0, 255, 0, 0.4));
    }
  }
`;
//#endregion
const EmployeeInformation = (props) => {



  //#region redux
  const dispatch = useDispatch();
  const timelineData = useSelector(selectTimelineData);                     // timeline data object
  const usedTheme = useSelector(selectUsedTheme);
  const employeeConversations = useSelector(selectEmployeeConversations);
  const dbUser = useSelector(selectFirebaseInfo);                                       // logged in user
  const firebaseUser = useSelector(selectFirebaseUser);
  const timelineAppSettings = useSelector(selectTimelineAppSettings);

  //#endregion

  // When QR-code shift is enabled (DUB), employees start/end their shift from the
  // app by scanning a QR code. Hide the portal's on-behalf-of Log In / Log Out so
  // it can't create shifts/sessions that skip the QR flow. Exempt roles (e.g.
  // supervisors) may still be logged in/out from the portal.
  const qrExemptRoles = timelineAppSettings?.locationQRExemptRoles || [];
  const qrShiftBlocked = !!timelineAppSettings?.enableLocationQRShift && !qrExemptRoles.includes(props?.employee?.role);

  //#region state
  const [hasBreakTask, setHasBreakTask] = useState(false)                   //check if employee has a break task
  const [shiftStart, setShiftStart] = useState(null);
  const [shiftEnd, setShiftEnd] = useState(null);

  // status handlers
  const [employeeStatus, setEmployeeStatus] = useState("offline");
  const [emojiColor, setEmojiColor] = useState("grey")                      //handle the color of emoji depend on break task status
  const [equipment, setEquipment] = useState('')                            //handle the equipment of the employee
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [displayShiftModal, setDisplayShiftModal] = useState(false);
  const [shouldGlow, setShouldGlow] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);                     // { mouseX, mouseY } or null
  const contextMenuRef = useRef(null);

  //#endregion

  //#region functions
  // open options modal


  // navigate to employee profile
  const handleEmployeeClick = () => {
    dispatch(setSelectedEmployeeProfile(props?.employee));
    setShowEmployeeModal(true);
  };

  // open context menu
  const handleOpenOptions = (e) => {
    e.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: e.clientX + 2, mouseY: e.clientY - 6 }
        : null,
    );
  };

  // turn employee to offline/online status
  const turnEmployeeStatus = () => {
    const newStatus = props?.employee?.status !== "offline" ? "offline" : "online";
    const payLoad = {
      userId: props.employee.userId,
      body: {
        status: newStatus
      }
    }
    if (props.employee && props.employee.userId) dispatch(updateEmployee(payLoad));
  }

  // get shift hours and minutes
  const getShiftHoursAndMinutes = (date) => {
    const hours = String(new Date(convertTimeStampToDate(date)).getHours()).padStart(2, '0')
    const minutes = String(new Date(convertTimeStampToDate(date)).getMinutes()).padStart(2, '0')
    // return { hours, minutes }
    return `${hours}:${minutes}`
  };


  // open chat
  const handleOpenChat = () => {
    // if (dbUser.details.authority === "JER") return;
    const convId = `${props?.employee?.employeeId}_${props?.employee?.airport}`
    dispatch(setSelectedForChat({
      employee: props?.employee,
      conversation: employeeConversations?.find((conv) => conv?._id === convId) ?? null
    }));

  };
  //#endregion

  //#region useEffect  

  useEffect(() => {
    let employeeId = props.id;
    const allJobs = Object.values(timelineData.columns).map(column => column.jobs)?.flat();  //access the columns object and flat items since we don't care with hours
    //CHECK IF THIS EMPLOYEE HAS ANY BREAK TASK
    const breakTasksList = allJobs?.filter(job => job.isBreakTask === true && job.employeeId === employeeId && (job.active === true || job.completed === true))

    //CHECK IF ANY OF THIS EMPLOYEE'S BREAK TASKS IS ACTIVE OR COMPLETED
    if (breakTasksList.length) {
      setHasBreakTask(true)

      // Check if there is at least one active task
      if (breakTasksList.some(task => task.active === true)) {
        setEmojiColor(Colors.PRM_LIGHT_BLUE); // make icon color blue
      }
      // Check if there is at least one completed task
      if (breakTasksList.some(task => task.completed === true)) {
        setEmojiColor("green"); // make icon color green

      }
    }
  }, [timelineData]);

  // status shift equipment listener
  useEffect(() => {

    /* check if employee has shift and is online and */
    if (props?.employee?.shift?.end && props?.employee?.status === "online") {

      setShiftStart(getShiftHoursAndMinutes(props?.employee?.shift?.start) ?? "");
      setShiftEnd(getShiftHoursAndMinutes(props?.employee?.shift?.end) ?? "");
    }

    // check if employee has status and set emoji color
    if (props?.employee?.status) {
      setEmployeeStatus(props.employee.status.charAt(0).toUpperCase() + props.employee.status.slice(1));
      if (props.employee.status === "online") {
        setEmojiColor("green");
      } else {
        setEmojiColor("grey");
      }
    }

    // check if employee has equipment
    if (props?.employee?.employeeEquipment) {
      setEquipment(props?.employee?.employeeEquipment)
    }

  }, [props?.employee]);

  useEffect(() => {
    // if (dbUser.details.authority === "JER") return setShouldGlow(false);
    if (props?.employee?.status === "offline") return setShouldGlow(false);
    if (Array.isFullArray(employeeConversations)) {
      const hasUnseenMsg = employeeConversations?.filter((conv) => conv?._id === `${props?.employee?.employeeId}_${props?.employee?.airport}` && conv?.lastMessage?.status !== "seen" && conv?.lastMessage?.senderId !== firebaseUser?.uid);
      if (Array.isFullArray(hasUnseenMsg)) setShouldGlow(true);
      else setShouldGlow(false);
    } else setShouldGlow(false);
  }, [props?.employee, employeeConversations])

  // Close contextMenu when clicked outside
  useEffect(() => {
    if (!contextMenu) return;

    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contextMenu]);

  //#endregion

  return (
    <>
      <div
        onContextMenu={handleOpenOptions}
        style={{
          opacity: employeeStatus == 'offline' ? 0.6 : 1.0,
          width: "100%", height: '100%', position: "relative",
          borderStyle: 'solid', borderWidth: 1, borderColor: 'lightgray',
          cursor: "pointer",
          display: "flex",
        }}>

        {/* profile picture + name */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', justifyContent: 'space-evenly' }}>

          {/* emp info div */}
          <div style={{ display: 'flex', flexDirection: "row", alignItems: "center", justifyContent: 'space-around', width: '100%', alignSelf: 'center', height: '100%' }}>

            {/* employee circle */}
            <div onClick={handleOpenChat}>
              <EmployeeProfilePicture
                employee={props?.employee}
                employeeStatus={employeeStatus}
                showStatus={true}
                showName={true}
                shift={shiftStart && shiftEnd ? [shiftStart, shiftEnd] : null}
                glow={shouldGlow}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', height: '100%', alignSelf: 'flex-end' }}>

              {/* chat icon */}
              {shouldGlow ? <GlowingIcon
                size={20}
                glow={shouldGlow}
                data-tippy-content="Chat"
                onClick={handleOpenChat}
              /> :
                <div></div>}


              {/* options icons */}
              <div style={{ alignSelf: 'flex-end' }}>
                <SlOptions
                  onClick={(e) => setContextMenu({ mouseX: e.clientX + 20, mouseY: e.clientY - 30 })}
                  style={{ cursor: "pointer" }}
                  color={Colors.GRAY_DARK}
                  size={18}
                  data-tippy-content="Options"
                />
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Employee Overview Modal */}
      <ActionsModal
        title="Employee Overview"
        subtitle="Detailed profile and analytics"
        show={showEmployeeModal}
        handleClose={() => setShowEmployeeModal(false)}
        isTimeline={true}
      >
        <EmployeeModal closeEmployeeModal={() => setShowEmployeeModal(false)} />
      </ActionsModal>

      {/* Employee Shift Modal */}
      <ActionsModal
        title="Employee shift"
        subtitle="Select the start and end of employee's shift,you can cheese predefined start/end shift or custom shifts"
        show={displayShiftModal}
        handleClose={() => setDisplayShiftModal(false)}
        isTimeline={true}
      >
        <AddEmployeeShift closeModal={() => setDisplayShiftModal(false)} employee={props.employee} />
      </ActionsModal>

      {/* Employee context options Modal */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          style={{
            position: "fixed",
            top: contextMenu.mouseY,
            left: contextMenu.mouseX,
            background: "white",
            border: "1px solid lightgray",
            borderRadius: 6,
            padding: 10,
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 12,
            zIndex: 99999,
            width: 165,
            height: "auto"
          }}
          onClick={() => setContextMenu(null)}
        >

          {/* employee details */}
          <div style={{ display: 'flex', cursor: 'pointer', width: '100%', gap: '1em', padding: '5px 0' }} onClick={handleEmployeeClick}>
            <FiChevronRight
              onClick={handleEmployeeClick}
              style={{ cursor: "pointer" }}
              color={Colors.PRM_LIGHT_BLUE}
              size={20}
              data-tippy-content="Employee Details"
            />
            <label style={{ fontSize: "12px", fontWeight: 'bold', color: 'grey', cursor: 'pointer', textAlign: 'left' }}>Employee Details</label>
          </div>

          {/* Emp activity */}
          {/* <div style={{ display: 'flex', cursor: 'pointer', width: '100%', gap: '1em', padding: '5px 0' }} onClick={handleEmpActivityClick}>
            <FiArrowUpRight
              onClick={handleEmpActivityClick}
              style={{ cursor: "pointer" }}
              color={Colors.PRM_LIGHT_BLUE}
              size={20}
              data-tippy-content="Employee Activity"
            />
            <label style={{ fontSize: "12px", fontWeight: 'bold', color: 'grey', cursor: 'pointer', textAlign: 'left' }}>Employee Activity</label>
          </div> */}

          {/* create break */}
          {props?.employeeShift && (
            <div style={{ display: 'flex', cursor: 'pointer', width: '100%', gap: '1em', padding: '5px 0' }} onClick={props?.openBreakTaskModal}>
              <FiCoffee
                onClick={props?.openBreakTaskModal}
                style={{ cursor: "pointer" }}
                color={"lightgrey"}
                size={20}
                data-tippy-content="Create Break"
              />
              <label style={{ fontSize: "12px", fontWeight: 'bold', color: 'grey', cursor: 'pointer', textAlign: 'left' }}> Create Break </label>
            </div>
          )}

          {/* log out */}
          {props?.employee?.status !== "offline" && !qrShiftBlocked && (
            <div style={{ display: 'flex', cursor: 'pointer', width: '100%', gap: '1em', padding: '5px 0' }} onClick={turnEmployeeStatus}>
              <FiLogOut
                onClick={turnEmployeeStatus}
                style={{ cursor: "pointer" }}
                color={"red"}
                size={20}
                data-tippy-content="Log Employee Out"
              />
              <label style={{ fontSize: "12px", fontWeight: 'bold', color: 'grey', cursor: 'pointer', textAlign: 'left' }}> Log Out </label>
            </div>
          )}

          {/* log in */}
          {props?.employee?.status === "offline" && !qrShiftBlocked && (
            <div style={{ display: 'flex', cursor: 'pointer', width: '100%', gap: '1em', padding: '5px 0' }} onClick={() => setDisplayShiftModal(true)}>
              <FiLogIn
                onClick={() => setDisplayShiftModal(true)}
                style={{ cursor: "pointer" }}
                color={"green"}
                size={20}
                data-tippy-content="Log Employee In"
              />
              <label style={{ fontSize: "12px", fontWeight: 'bold', color: 'grey', cursor: 'pointer', }}> Log In </label>
            </div>
          )}
        </div>
      )}

    </>
  );
};

export default EmployeeInformation;